/**
 * `/ai` — AI 영상 생성 (3-stage flow)
 * 핸드오프: docs/handoff/video-editor/project/v3_6/v3-6-ai.jsx
 *
 * Stage 1 (PROMPT)     — 프롬프트 + 톤·길이·비율 + 추천
 * Stage 2 (GENERATING) — orb 진행 + phase steps (백엔드 파이프라인 폴링)
 * Stage 3 (REVIEW)     — 씬 그리드 + 스크립트 + 사용 요소 + 변형 옵션
 *
 * 백엔드: makerStore.createLongform()
 * 라우팅: home의 CommandDock에서 query params(?prompt=&tone=&length=&ratio=)로 진입 시 자동 진행
 */
'use client'

import { Suspense, useEffect, useMemo, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import V36TopNav from '@/components/layout/V36TopNav'
import { V3Icon, type V3IconName } from '@/components/icons/V3Icon'
import { useMakerStore } from '@/store/makerStore'
import type { LongformAutoConfig } from '@/types'

const STAGES = ['prompt', 'generating', 'review'] as const
type Stage = (typeof STAGES)[number]

const TONES = ['자동', '따뜻한', '차가운', '빠른', '감성적', '클린']
const LENGTHS = ['15초', '30초', '1분', '1분+']
const RATIOS = ['9:16', '1:1', '16:9'] as const
type Ratio = (typeof RATIOS)[number]

const SUGGESTIONS = [
  { tag: '릴스', prompt: '신메뉴 마카롱 클로즈업, 핑크 톤 30초 릴스', duration: '0:30', ratio: '9:16' },
  { tag: '광고', prompt: '프리미엄 헤드폰 언박싱, 시네마틱 슬로우 컷', duration: '0:45', ratio: '16:9' },
  { tag: '교육', prompt: 'React Hooks 입문 1분 인트로, 모션 그래픽', duration: '1:00', ratio: '16:9' },
  { tag: '브이로그', prompt: '주말 캠핑 하이라이트, 따뜻한 필름 톤 60초', duration: '1:00', ratio: '9:16' },
]

const lengthToSeconds = (label: string): number =>
  ({ '15초': 15, '30초': 30, '1분': 60, '1분+': 90 } as Record<string, number>)[label] ?? 30

export default function AIPage() {
  return (
    <>
      <V36TopNav />
      <Suspense fallback={null}>
        <AIPageInner />
      </Suspense>
    </>
  )
}

function AIPageInner() {
  const sp = useSearchParams()
  const router = useRouter()
  const { createLongform, fetchPipeline, currentPipeline, currentProject, error, reset } =
    useMakerStore()

  // 진입 시 이전 파이프라인 상태 초기화 (다른 generate 호출의 잔존 상태 방지)
  useEffect(() => {
    reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 초기 상태 — query params에서 프리필
  const [stage, setStage] = useState<Stage>('prompt')
  const [prompt, setPrompt] = useState(sp?.get('prompt') ?? '')
  const [tone, setTone] = useState<string>(sp?.get('tone') ?? '자동')
  const [length, setLength] = useState<string>(sp?.get('length') ?? '30초')
  const [ratio, setRatio] = useState<Ratio>(((sp?.get('ratio') as Ratio) ?? '9:16') as Ratio)
  const [progress, setProgress] = useState(0)
  const [pipelineId, setPipelineId] = useState<string | null>(null)
  const [projectId, setProjectId] = useState<string | null>(null)

  // 진입 시 query에 prompt 있으면 곧장 generating으로 (홈 CommandDock 통해 들어온 케이스)
  useEffect(() => {
    if (sp?.get('prompt') && sp.get('autostart') !== '0') {
      // 단, 자동 시작은 prompt가 의미 있는 길이일 때만
      if ((sp.get('prompt') ?? '').trim().length >= 4) {
        void submit()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // generating 단계 진입 시: 백엔드 호출 + 폴링
  const submit = async () => {
    if (!prompt.trim()) return
    setStage('generating')
    setProgress(0)

    const config: LongformAutoConfig = {
      prompt: `${prompt} | 톤:${tone} | 비율:${ratio}`,
      genre: 'story',
      targetDuration: lengthToSeconds(length),
      language: 'ko',
      imageStyle: 'realistic',
      addBgm: true,
    }

    try {
      const result = await createLongform(config)
      setPipelineId(result.pipelineId)
      setProjectId(result.projectId)
    } catch (e) {
      // 백엔드 실패 시: 시뮬레이션 모드로 폴백 (UX 끊김 방지)
      console.warn('[/ai] createLongform failed, falling back to simulation', e)
    }
  }

  // 진행률 업데이트 — 실제 파이프라인 우선, 없으면 가짜 진행
  useEffect(() => {
    if (stage !== 'generating') return

    if (!pipelineId) {
      // 시뮬레이션 진행
      const id = setInterval(() => {
        setProgress((p) => {
          const next = p + 4 + Math.random() * 6
          if (next >= 100) {
            clearInterval(id)
            setTimeout(() => setStage('review'), 400)
            return 100
          }
          return next
        })
      }, 220)
      return () => clearInterval(id)
    }

    // 실제 파이프라인 폴링
    const id = setInterval(async () => {
      try {
        await fetchPipeline(pipelineId)
      } catch {
        /* noop */
      }
    }, 1500)
    return () => clearInterval(id)
  }, [stage, pipelineId, fetchPipeline])

  // 파이프라인 진행률을 progress에 매핑 — 스텝 평균으로 계산
  useEffect(() => {
    if (!currentPipeline || stage !== 'generating') return
    const steps = currentPipeline.steps ?? []
    const stepProgress =
      steps.length === 0
        ? 0
        : steps.reduce((sum, s) => {
            if (s.status === 'completed') return sum + 100
            if (s.status === 'running') return sum + (s.progress ?? 0)
            return sum
          }, 0) / steps.length
    setProgress((prev) => Math.min(100, Math.max(prev, stepProgress)))
    if (currentPipeline.status === 'completed') {
      setTimeout(() => setStage('review'), 400)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPipeline, stage])

  const navigateToEditor = () => {
    const id = projectId ?? currentProject?.id
    if (id) router.push(`/editor/${id}`)
    else router.push('/projects')
  }

  return (
    <main
      className="v36-fade-in"
      style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 24px 80px' }}
    >
      {/* Stage 인디케이터 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
        <button
          className="v36-btn v36-btn--ghost v36-btn--sm"
          onClick={() => router.push('/')}
          style={{ padding: '0 10px 0 8px' }}
        >
          <V3Icon name="arrowL" size={14} />
          <span>홈</span>
        </button>
        <span style={{ color: 'var(--v36-ink-5)' }}>/</span>
        <span className="v36-label" style={{ color: 'var(--v36-ink-2)' }}>
          AI 생성
        </span>
        <span style={{ flex: 1 }} />
        <div className="v36-pc-only" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {STAGES.map((s, i) => (
            <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {i > 0 && <span style={{ width: 24, height: 1, background: 'var(--v36-line)' }} />}
              <span
                className="v36-mono"
                style={{
                  fontSize: 10,
                  padding: '5px 10px',
                  borderRadius: 999,
                  background: stage === s ? 'var(--v36-violet)' : 'transparent',
                  color:
                    stage === s
                      ? '#fff'
                      : STAGES.indexOf(stage) > i
                        ? 'var(--v36-ink-2)'
                        : 'var(--v36-ink-4)',
                  border: `1px solid ${stage === s ? 'var(--v36-violet)' : 'var(--v36-line)'}`,
                  textTransform: 'uppercase',
                  letterSpacing: '0.10em',
                }}
              >
                {String(i + 1).padStart(2, '0')} {s === 'prompt' ? 'PROMPT' : s === 'generating' ? 'GEN' : 'REVIEW'}
              </span>
            </span>
          ))}
        </div>
      </div>

      {error && (
        <div
          className="v36-glass"
          style={{ padding: 14, borderRadius: 12, marginBottom: 16, color: 'var(--v36-error)', fontSize: 13 }}
        >
          ⚠️ {error}
        </div>
      )}

      {stage === 'prompt' && (
        <PromptStage
          prompt={prompt}
          setPrompt={setPrompt}
          tone={tone}
          setTone={setTone}
          length={length}
          setLength={setLength}
          ratio={ratio}
          setRatio={setRatio}
          onSubmit={submit}
        />
      )}
      {stage === 'generating' && <GenStage progress={progress} prompt={prompt} />}
      {stage === 'review' && (
        <ReviewStage
          prompt={prompt}
          ratio={ratio}
          onRegen={() => {
            setStage('prompt')
            setProgress(0)
            setPipelineId(null)
          }}
          onEdit={navigateToEditor}
        />
      )}
    </main>
  )
}

/* ════════════════════════════════════════════════════════════ */
/* Stage 1 — PROMPT                                             */
/* ════════════════════════════════════════════════════════════ */
function PromptStage({
  prompt,
  setPrompt,
  tone,
  setTone,
  length,
  setLength,
  ratio,
  setRatio,
  onSubmit,
}: {
  prompt: string
  setPrompt: (v: string) => void
  tone: string
  setTone: (v: string) => void
  length: string
  setLength: (v: string) => void
  ratio: Ratio
  setRatio: (v: Ratio) => void
  onSubmit: () => void
}) {
  return (
    <div>
      <div className="v36-label" style={{ marginBottom: 14 }}>
        STEP 01 / 03
      </div>
      <h1
        className="v36-display"
        style={{ fontSize: 'clamp(36px, 6vw, 64px)', margin: 0, marginBottom: 20 }}
      >
        어떤 영상을 만들까요?
      </h1>
      <p style={{ color: 'var(--v36-ink-3)', fontSize: 16, marginBottom: 32, maxWidth: 640 }}>
        한 줄로 충분합니다. AI가 씬을 짜고 트랜지션·자막을 추가합니다.
      </p>

      <div
        className="v36-glass-strong"
        style={{
          borderRadius: 24,
          padding: 8,
          boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
          border: '1px solid rgba(255,255,255,0.14)',
        }}
      >
        <div style={{ padding: '20px 20px 12px', display: 'flex', gap: 12 }}>
          <V3Icon
            name="sparkle"
            size={22}
            style={{ color: 'var(--v36-violet)', flexShrink: 0, marginTop: 2 }}
          />
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="만들고 싶은 영상을 자유롭게 적어주세요…"
            style={{
              flex: 1,
              minHeight: 80,
              border: 0,
              background: 'transparent',
              fontSize: 18,
              lineHeight: 1.5,
              color: '#fff',
              outline: 'none',
              resize: 'vertical',
              fontFamily: 'inherit',
            }}
          />
        </div>

        <div
          style={{
            borderTop: '1px solid rgba(255,255,255,0.08)',
            padding: '16px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <OptionRow label="톤" options={TONES} value={tone} onChange={setTone} />
          <OptionRow label="길이" options={LENGTHS} value={length} onChange={setLength} />
          <OptionRow
            label="비율"
            options={RATIOS as readonly string[]}
            value={ratio}
            onChange={(v) => setRatio(v as Ratio)}
            mono
          />
        </div>

        <div
          style={{
            borderTop: '1px solid rgba(255,255,255,0.08)',
            padding: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <button className="v36-btn v36-btn--ghost v36-btn--sm">
            <V3Icon name="upload" size={14} />
            <span>레퍼런스 업로드</span>
          </button>
          <button className="v36-btn v36-btn--ghost v36-btn--sm">
            <V3Icon name="image" size={14} />
            <span>이미지 첨부</span>
          </button>
          <span style={{ flex: 1 }} />
          <span className="v36-mono" style={{ fontSize: 11, color: 'var(--v36-ink-4)' }}>
            {prompt.length} / 500
          </span>
          <button
            className="v36-btn v36-btn--neon"
            onClick={onSubmit}
            disabled={!prompt.trim()}
            style={{ opacity: prompt.trim() ? 1 : 0.5 }}
          >
            <V3Icon name="bolt" size={15} />
            <span>생성하기</span>
          </button>
        </div>
      </div>

      <div style={{ marginTop: 56 }}>
        <div className="v36-label" style={{ marginBottom: 16 }}>
          다른 사람들이 만든 프롬프트
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 12,
          }}
        >
          {SUGGESTIONS.map((s, i) => (
            <button
              key={i}
              onClick={() => setPrompt(s.prompt)}
              className="v36-card v36-card--hover"
              style={{ textAlign: 'left', padding: 16 }}
            >
              <div
                className="v36-mono"
                style={{
                  fontSize: 10,
                  color: 'var(--v36-ink-4)',
                  marginBottom: 8,
                  textTransform: 'uppercase',
                  letterSpacing: '0.10em',
                }}
              >
                {s.tag}
              </div>
              <div style={{ fontSize: 14, color: 'var(--v36-ink-2)', lineHeight: 1.5 }}>
                &ldquo;{s.prompt}&rdquo;
              </div>
              <div
                style={{
                  marginTop: 12,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span className="v36-chip v36-chip--mono" style={{ height: 22, fontSize: 9 }}>
                  {s.duration} · {s.ratio}
                </span>
                <V3Icon name="arrowR" size={14} style={{ color: 'var(--v36-ink-4)' }} />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

function OptionRow({
  label,
  options,
  value,
  onChange,
  mono,
}: {
  label: string
  options: readonly string[]
  value: string
  onChange: (v: string) => void
  mono?: boolean
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <span className="v36-label" style={{ fontSize: 10, width: 36, flexShrink: 0 }}>
        {label}
      </span>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {options.map((o) => (
          <button
            key={o}
            onClick={() => onChange(o)}
            className={`v36-chip ${value === o ? 'v36-chip--active' : ''} ${mono ? 'v36-mono' : ''}`}
            style={{ height: 28, fontSize: mono ? 11 : 12 }}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  )
}

/* ════════════════════════════════════════════════════════════ */
/* Stage 2 — GENERATING                                         */
/* ════════════════════════════════════════════════════════════ */
function GenStage({ progress, prompt }: { progress: number; prompt: string }) {
  const phases = useMemo(
    () => [
      { p: 20, label: '프롬프트 분석' },
      { p: 50, label: '씬 구성' },
      { p: 75, label: '트랜지션 · BGM' },
      { p: 100, label: '자막 생성' },
    ],
    []
  )

  return (
    <div style={{ paddingTop: 60, textAlign: 'center' }}>
      <div className="v36-label" style={{ marginBottom: 14 }}>
        STEP 02 / 03
      </div>
      <h1 className="v36-display" style={{ fontSize: 'clamp(36px, 6vw, 64px)', margin: 0, marginBottom: 16 }}>
        영상을 만들고 있습니다
      </h1>
      <p
        style={{
          color: 'var(--v36-ink-3)',
          fontSize: 15,
          maxWidth: 600,
          margin: '0 auto 56px',
        }}
      >
        &ldquo;{prompt}&rdquo;
      </p>

      {/* Orb */}
      <div style={{ position: 'relative', width: 240, height: 240, margin: '0 auto 56px' }}>
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background:
              'conic-gradient(from 0deg, var(--v36-violet), var(--v36-cyan), var(--v36-lime), var(--v36-violet))',
            filter: 'blur(28px)',
            opacity: 0.7,
            animation: 'v36-spin 8s linear infinite',
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 24,
            borderRadius: '50%',
            background: 'var(--v36-bg-2)',
            border: '1px solid rgba(255,255,255,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <div
            className="v36-mono"
            style={{
              fontSize: 48,
              fontWeight: 600,
              color: '#fff',
              letterSpacing: '-0.04em',
            }}
          >
            {Math.floor(progress)}
            <span style={{ fontSize: 24, color: 'var(--v36-ink-3)' }}>%</span>
          </div>
          <div
            className="v36-mono"
            style={{
              fontSize: 10,
              color: 'var(--v36-ink-3)',
              textTransform: 'uppercase',
              letterSpacing: '0.16em',
            }}
          >
            GENERATING
          </div>
        </div>
      </div>

      {/* Phase steps */}
      <div
        className="v36-glass-strong"
        style={{ maxWidth: 480, margin: '0 auto', padding: 24, borderRadius: 18 }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {phases.map((ph, i) => {
            const done = progress >= ph.p
            const active = !done && progress >= (i === 0 ? 0 : phases[i - 1].p)
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  opacity: done ? 1 : active ? 0.9 : 0.4,
                }}
              >
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: done ? 'var(--v36-violet)' : 'transparent',
                    border: `1.5px solid ${done ? 'var(--v36-violet)' : 'rgba(255,255,255,0.18)'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {done && <V3Icon name="check" size={11} stroke="#fff" />}
                  {active && (
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: 'var(--v36-violet)',
                        animation: 'v36-pulse 1.2s ease-in-out infinite',
                      }}
                    />
                  )}
                </div>
                <span style={{ fontSize: 14, color: done ? '#fff' : 'var(--v36-ink-2)' }}>{ph.label}</span>
                <span style={{ flex: 1 }} />
                {done && (
                  <span className="v36-mono" style={{ fontSize: 10, color: 'var(--v36-success)' }}>
                    완료
                  </span>
                )}
                {active && (
                  <span className="v36-mono" style={{ fontSize: 10, color: 'var(--v36-violet)' }}>
                    진행 중…
                  </span>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/* ════════════════════════════════════════════════════════════ */
/* Stage 3 — REVIEW                                             */
/* ════════════════════════════════════════════════════════════ */
type Tone = 'violet' | 'rose' | 'amber' | 'lime'
const SCENES: Array<{ tone: Tone; duration: string; title: string; caption: string }> = [
  { tone: 'violet', duration: '0:08', title: '오프닝', caption: '오늘부터, 분당의 새 카페' },
  { tone: 'rose', duration: '0:07', title: '메뉴 클로즈업', caption: '매일 아침 직접 내리는 핸드드립' },
  { tone: 'amber', duration: '0:08', title: '공간', caption: '햇살이 잘 드는 창가 자리' },
  { tone: 'lime', duration: '0:07', title: '엔딩', caption: 'Open · 매일 8시 ~ 22시' },
]

function ReviewStage({
  prompt,
  ratio,
  onRegen,
  onEdit,
}: {
  prompt: string
  ratio: Ratio
  onRegen: () => void
  onEdit: () => void
}) {
  const [active, setActive] = useState(0)

  const aspect: string = ratio === '9:16' ? '9 / 16' : ratio === '1:1' ? '1 / 1' : '16 / 9'
  const playerWidth: string = ratio === '9:16' ? 'min(280px, 100%)' : '100%'

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          marginBottom: 24,
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div className="v36-label" style={{ marginBottom: 12 }}>
            STEP 03 / 03 · 완성
          </div>
          <h1 className="v36-h1" style={{ margin: 0, marginBottom: 8 }}>
            초안이 준비됐어요
          </h1>
          <p style={{ color: 'var(--v36-ink-3)', fontSize: 14, margin: 0 }}>
            &ldquo;{prompt}&rdquo;
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="v36-btn v36-btn--sm" onClick={onRegen}>
            <V3Icon name="sparkle" size={14} />
            <span>다시 생성</span>
          </button>
          <button className="v36-btn v36-btn--sm" onClick={onEdit}>
            <V3Icon name="film" size={14} />
            <span>씬 에디터에서 편집</span>
          </button>
          <button className="v36-btn v36-btn--neon v36-btn--sm">
            <V3Icon name="download" size={14} />
            <span>내보내기</span>
          </button>
        </div>
      </div>

      <div
        className="v36-ai-review-grid"
        style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px', gap: 16 }}
      >
        {/* Player + scene strip */}
        <div className="v36-glass" style={{ padding: 16, borderRadius: 18 }}>
          <div
            className={`v36-thumb v36-thumb--${SCENES[active].tone}`}
            style={{
              aspectRatio: aspect,
              maxHeight: 480,
              borderRadius: 12,
              margin: '0 auto',
              width: playerWidth,
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'rgba(0,0,0,0.5)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255,255,255,0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <V3Icon name="play" size={22} fill="#fff" />
            </div>
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 24,
                textAlign: 'center',
                padding: '0 24px',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  padding: '8px 14px',
                  borderRadius: 8,
                  background: 'rgba(0,0,0,0.7)',
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 600,
                  lineHeight: 1.3,
                }}
              >
                {SCENES[active].caption}
              </span>
            </div>
            <div
              style={{
                position: 'absolute',
                left: 12,
                right: 12,
                bottom: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                color: '#fff',
              }}
            >
              <span className="v36-mono" style={{ fontSize: 11, opacity: 0.8 }}>
                00:0{active * 7 + 2} / 00:30
              </span>
              <div
                style={{
                  flex: 1,
                  height: 3,
                  borderRadius: 2,
                  background: 'rgba(255,255,255,0.2)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${(active + 1) * 25}%`,
                    height: '100%',
                    background: 'var(--v36-violet)',
                  }}
                />
              </div>
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <div className="v36-label" style={{ marginBottom: 10 }}>
              씬 {SCENES.length}개 · 30초
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${SCENES.length}, 1fr)`, gap: 8 }}>
              {SCENES.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  style={{
                    padding: 4,
                    borderRadius: 10,
                    border: `1.5px solid ${active === i ? 'var(--v36-violet)' : 'transparent'}`,
                    background: active === i ? 'rgba(124,92,255,0.10)' : 'transparent',
                    textAlign: 'left',
                  }}
                >
                  <div className={`v36-thumb v36-thumb--${s.tone}`} style={{ aspectRatio: '16/10', borderRadius: 6 }}>
                    <div
                      className="v36-mono"
                      style={{
                        position: 'absolute',
                        left: 4,
                        top: 4,
                        fontSize: 9,
                        padding: '1px 4px',
                        borderRadius: 3,
                        background: 'rgba(0,0,0,0.6)',
                        color: '#fff',
                      }}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <div
                      className="v36-mono"
                      style={{
                        position: 'absolute',
                        right: 4,
                        bottom: 4,
                        fontSize: 9,
                        padding: '1px 4px',
                        borderRadius: 3,
                        background: 'rgba(0,0,0,0.6)',
                        color: '#fff',
                      }}
                    >
                      {s.duration}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      marginTop: 6,
                      color: active === i ? '#fff' : 'var(--v36-ink-3)',
                      padding: '0 2px',
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {s.title}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Side: script + assets + variants */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="v36-glass" style={{ padding: 16, borderRadius: 14 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 12,
              }}
            >
              <span className="v36-label">스크립트</span>
              <button
                className="v36-btn v36-btn--ghost v36-btn--sm"
                style={{ height: 26, padding: '0 10px', fontSize: 11 }}
              >
                <V3Icon name="type" size={12} />
                <span>편집</span>
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {SCENES.map((s, i) => (
                <div
                  key={i}
                  onClick={() => setActive(i)}
                  style={{
                    display: 'flex',
                    gap: 10,
                    padding: '8px 10px',
                    borderRadius: 8,
                    background: active === i ? 'rgba(124,92,255,0.10)' : 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <span
                    className="v36-mono"
                    style={{ fontSize: 10, color: 'var(--v36-violet)', paddingTop: 2 }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div style={{ flex: 1, fontSize: 13, color: 'var(--v36-ink-2)', lineHeight: 1.5 }}>
                    {s.caption}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="v36-glass" style={{ padding: 16, borderRadius: 14 }}>
            <div className="v36-label" style={{ marginBottom: 12 }}>
              사용된 요소
            </div>
            <Used label="BGM" value="Lofi · Warm Sunday" icon="music" />
            <Used label="자막 폰트" value="Pretendard SemiBold" icon="type" />
            <Used label="트랜지션" value="Crossfade · 0.4s" icon="layers" />
            <Used label="컬러 그레이드" value="Warm Cinema 02" icon="image" />
          </div>

          <div className="v36-glass" style={{ padding: 16, borderRadius: 14 }}>
            <div className="v36-label" style={{ marginBottom: 12 }}>
              다른 버전 만들기
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {['더 빠르게', '더 차분하게', '다른 BGM', '1:1 비율', '60초 버전', '영문 자막'].map((v) => (
                <button key={v} className="v36-chip" style={{ height: 28, fontSize: 12 }}>
                  <V3Icon name="sparkle" size={11} />
                  <span>{v}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .v36-ai-review-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  )
}

function Used({ label, value, icon }: { label: string; value: string; icon: V3IconName }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 0',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 8,
          background: 'rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--v36-ink-3)',
        }}
      >
        <V3Icon name={icon} size={14} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          className="v36-mono"
          style={{
            fontSize: 9,
            color: 'var(--v36-ink-4)',
            textTransform: 'uppercase',
            letterSpacing: '0.10em',
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 13,
            color: '#fff',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </div>
      </div>
      <button className="v36-btn v36-btn--ghost v36-btn--sm" style={{ width: 28, height: 28, padding: 0 }}>
        <V3Icon name="dotsv" size={12} />
      </button>
    </div>
  )
}
